import { parseStartParams } from "@/utils/urlEncoder";
import { STORAGE_KEYS } from "./config";
import { local, session } from "./storage";
import type { UtmParams } from "./types";

const UTM_KEYS: (keyof UtmParams)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
];

const hasAnyUtm = (utm: UtmParams): boolean =>
  UTM_KEYS.some((k) => Boolean(utm[k]));

const pickUtm = (params: URLSearchParams): UtmParams => {
  const out: UtmParams = {};
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) out[key] = value;
  }
  return out;
};

/**
 * Read UTMs from the current launch URL and, as a fallback, from the Eitaa
 * `start_param` (deep-link payload). The start param carries a query string
 * (e.g. `utm_source=instagram&...`), base64url-encoded to survive Eitaa's
 * `A-Za-z0-9_-` charset limit; {@link parseStartParams} decodes both that and
 * the plain/URL-encoded form.
 */
const readLaunchUtm = (): UtmParams => {
  if (typeof window === "undefined") return {};

  const fromUrl = pickUtm(new URLSearchParams(window.location.search));
  if (hasAnyUtm(fromUrl)) return fromUrl;

  const fromStart = pickUtm(new URLSearchParams(parseStartParams()));
  if (hasAnyUtm(fromStart)) return fromStart;

  return {};
};

const readStored = (key: string): UtmParams | null => {
  const raw = (key === STORAGE_KEYS.utmFirst ? local : session).get(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as UtmParams;
  } catch {
    return null;
  }
};

/**
 * Re-derive the launch-scoped (last-touch) UTM. Call once per cold launch,
 * during analytics init. Implements the guide's launch rule:
 *
 *  - launch URL has UTMs  → active UTM = those (and seed `amz_utm_first` once)
 *  - launch URL has none  → active UTM = organic; the previous launch's UTM is
 *                           NOT resurrected.
 */
export const captureLaunchUtm = (): void => {
  const launchUtm = readLaunchUtm();

  if (hasAnyUtm(launchUtm)) {
    session.set(STORAGE_KEYS.utmActive, JSON.stringify(launchUtm));
    // First-touch is write-once and persists forever.
    if (!readStored(STORAGE_KEYS.utmFirst)) {
      local.set(STORAGE_KEYS.utmFirst, JSON.stringify(launchUtm));
    }
  } else {
    // Organic launch — drop any active UTM left over from a prior launch.
    session.remove(STORAGE_KEYS.utmActive);
  }
};

/**
 * The active (last-touch) UTM for THIS launch. Attach to every event and to the
 * buy request. Empty object means organic — omit the utm_* fields entirely.
 */
export const getActiveUtm = (): UtmParams => readStored(STORAGE_KEYS.utmActive) ?? {};

/**
 * The first-touch UTM ("how we originally acquired this user"). Persists
 * forever. Sent ONLY in the login/callback body — never on events.
 */
export const getFirstTouchUtm = (): UtmParams =>
  readStored(STORAGE_KEYS.utmFirst) ?? {};
